import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { cardsService, cardInvoicesService } from '../services/card-invoices.service';
import type { CardInvoiceItemKind } from '../services/card-invoices.service';

const CARDS_KEY = 'credit-cards';
const INVOICES_KEY = 'card-invoices';

// ── Cartões ──────────────────────────────────────────────

export function useCreditCards(companyId: string, params?: { active?: boolean }) {
  return useQuery({
    queryKey: [CARDS_KEY, companyId, params],
    queryFn: () => cardsService.list(companyId, params),
    enabled: !!companyId,
  });
}

export function useCreateCreditCard() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: Record<string, any>) => cardsService.create(data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [CARDS_KEY] });
    },
  });
}

export function useUpdateCreditCard() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: Record<string, any> }) =>
      cardsService.update(id, data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [CARDS_KEY] });
    },
  });
}

export function useRemoveCreditCard() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => cardsService.remove(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [CARDS_KEY] });
    },
  });
}

// ── Faturas ──────────────────────────────────────────────

export function useCardInvoices(
  cardId: string,
  params?: { status?: string; page?: number; limit?: number }
) {
  return useQuery({
    queryKey: [INVOICES_KEY, 'list', cardId, params],
    queryFn: () => cardInvoicesService.listByCard(cardId, params),
    enabled: !!cardId,
  });
}

export function useCardInvoice(invoiceId: string | undefined) {
  return useQuery({
    queryKey: [INVOICES_KEY, 'one', invoiceId],
    queryFn: () => cardInvoicesService.getOne(invoiceId!),
    enabled: !!invoiceId,
  });
}

export function useUploadCardInvoice() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (args: {
      cardId: string;
      referenceMonth: number;
      referenceYear: number;
      file: File;
    }) => cardInvoicesService.upload(args),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [INVOICES_KEY] });
    },
  });
}

export function usePatchInvoiceItem(invoiceId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      itemId,
      patch,
    }: {
      itemId: string;
      patch: Partial<{
        suggestedCategoryId: string | null;
        type: 'INCOME' | 'EXPENSE';
        kind: CardInvoiceItemKind;
        description: string;
        status: 'REJECTED' | 'PENDING';
        beneficiaryCompanyId: string | null;
      }>;
    }) => cardInvoicesService.patchItem(invoiceId, itemId, patch),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [INVOICES_KEY, 'one', invoiceId] });
    },
  });
}

export function useAddInvoiceItem(invoiceId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (item: {
      postDate: string;
      description: string;
      amount: number;
      kind?: CardInvoiceItemKind;
      type?: 'INCOME' | 'EXPENSE';
      suggestedCategoryId?: string | null;
    }) => cardInvoicesService.addItem(invoiceId, item),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [INVOICES_KEY, 'one', invoiceId] });
    },
  });
}

export function useApproveInvoice(invoiceId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { itemIds: string[] }) => cardInvoicesService.approve(invoiceId, body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [INVOICES_KEY, 'one', invoiceId] });
      qc.invalidateQueries({ queryKey: ['financial-ops', 'transactions'] });
    },
  });
}

export function useUnapproveInvoice(invoiceId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { itemIds: string[] }) => cardInvoicesService.unapprove(invoiceId, body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [INVOICES_KEY, 'one', invoiceId] });
      qc.invalidateQueries({ queryKey: ['financial-ops', 'transactions'] });
    },
  });
}

export function useRejectAllInvoice(invoiceId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => cardInvoicesService.rejectAll(invoiceId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [INVOICES_KEY, 'one', invoiceId] });
    },
  });
}

export function useSettlementSuggestions(invoiceId: string, enabled: boolean) {
  return useQuery({
    queryKey: [INVOICES_KEY, 'settlement-suggestions', invoiceId],
    queryFn: () => cardInvoicesService.suggestSettlement(invoiceId),
    enabled: enabled && !!invoiceId,
  });
}

export function useConfirmSettlement(invoiceId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (settlementTransactionId: string) =>
      cardInvoicesService.confirmSettlement(invoiceId, settlementTransactionId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [INVOICES_KEY, 'one', invoiceId] });
      qc.invalidateQueries({ queryKey: ['financial-ops', 'transactions'] });
    },
  });
}

export function useUnlinkSettlement(invoiceId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => cardInvoicesService.unlinkSettlement(invoiceId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [INVOICES_KEY, 'one', invoiceId] });
      qc.invalidateQueries({ queryKey: ['financial-ops', 'transactions'] });
    },
  });
}

export function useCloseInvoice(invoiceId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => cardInvoicesService.close(invoiceId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [INVOICES_KEY] });
    },
  });
}

export function useDeleteCardInvoice() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (invoiceId: string) => cardInvoicesService.remove(invoiceId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [INVOICES_KEY] });
    },
  });
}

export function useCardInvoicePendencies(closingId: string | undefined) {
  return useQuery({
    queryKey: [INVOICES_KEY, 'pendencies', closingId],
    queryFn: () => cardInvoicesService.pendencies(closingId!),
    enabled: !!closingId,
  });
}
